import { useRef, useState } from "react";
import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

const parseOptionalNumber = (value) =>
  value === null || value === undefined || value === ""
    ? undefined
    : Number(value);

function parseReceivingInvoice(invoice) {
  return {
    receiving_invoice_id: Number(invoice.receiving_invoice_id) || 0,
    receiving_invoice_number: invoice.receiving_invoice_number,
    receiving_lot_number: invoice.receiving_lot_number,
    receiving_date: invoice.receiving_date ?? null,
    total_received_kg: parseOptionalNumber(invoice.total_received_kg),
    total_received_lbs: parseOptionalNumber(invoice.total_received_lbs),
    total_received_pcs: parseOptionalNumber(invoice.total_received_pcs),
    receiving_carton_count: parseOptionalNumber(invoice.receiving_carton_count),
    supplier_id: invoice.supplier_id == null ? null : Number(invoice.supplier_id),
    supplier_name: invoice.supplier_name ?? null,
    warehouse_id: parseOptionalNumber(invoice.warehouse_id),
    warehouse_name: invoice.warehouse_name ?? null,
    warehouse_location_id: parseOptionalNumber(invoice.warehouse_location_id),
    warehouse_location_name: invoice.warehouse_location_name ?? null,
  };
}

function parseShippedItem(item) {
  return {
    order_id: Number(item.order_id),
    lot_number: item.lot_number,
    total_in_kg: parseFloat(item.total_in_kg) || 0,
    total_in_lbs: parseFloat(item.total_in_lbs) || 0,
    total_in_pieces: parseFloat(item.total_in_pieces) || 0,
    order: {
      id: Number(item.order?.id),
      invoice_number: item.order?.invoice_number ?? item.order?.po_number ?? null,
      po_number: item.order?.po_number ?? null,
      customer_name: item.order?.customer_name ?? null,
    },
  };
}

export function parseSupplierSource(sourceObj) {
  if (!sourceObj) return null;

  const receivingInvoices = Array.isArray(sourceObj.receiving_invoices)
    ? sourceObj.receiving_invoices
    : sourceObj.receiving_invoice_id != null
    ? [sourceObj]
    : [];

  const parsedSource = {
    ...sourceObj,
    is_wip: Boolean(sourceObj.is_wip),
    receiving_invoices: receivingInvoices.map(parseReceivingInvoice),
    shipped_items: (sourceObj.shipped_items || []).map(parseShippedItem),
  };

  return sourceObj.is_wip
    ? { ...parsedSource, wip_id: parseInt(sourceObj.wip_id) || 0 }
    : parsedSource;
}

export function parseDirectChild(child) {
  return {
    ...child,
    used_kg: parseFloat(child.used_kg) || 0,
    used_pcs: parseFloat(child.used_pcs) || 0,
    child_total_produced_kg: parseFloat(child.child_total_produced_kg) || 0,
    child_total_produced_pcs: parseInt(child.child_total_produced_pcs) || 0,
  };
}

export function TraceabilityViewModel(logout) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);

  // Step state
  const [lookup, setLookup] = useState("");
  const [err, setErr] = useState("");

  // Resolved current WIP context
  const [currentWip, setCurrentWip] = useState(null); // { id, lot_number }
  const [incomingPlus, setIncomingPlus] = useState([]); // [{ plu, incoming_kg, incoming_pcs, parent_wip_count }]

  // Chosen PLU within the current WIP (picked once)
  const [selectedPlu, setSelectedPlu] = useState(null);

  // Detail for chosen PLU (first level only)
  const [byParent, setByParent] = useState([]);
  const [byLot, setByLot] = useState([]);

  // Breadcrumbs for drill
  const [crumbs, setCrumbs] = useState([]); // [{ wip_id, label }]

  // (kept for backward compatibility views)
  const [directParents, setDirectParents] = useState([]);

  // Supplier item tracing state (Trace Children)
  const [lotNumber, setLotNumber] = useState(null);
  const [source, setSource] = useState(null); // Single source object
  const [directChildren, setDirectChildren] = useState([]);
  // Legacy receiving state for backward compatibility
  const [receiving, setReceiving] = useState(null);
  const latestSupplierRequestId = useRef(0);

  const doLookup = async function (raw, reportType = 1) {
    setLoading(true);
    setErr("");
    
    // Reset state based on report type
    if (reportType === 1) {
      // Manufactured product tracing
      setReceiving(null);
      setDirectChildren([]);
      let callResult = await makeGetApiCall(
        `trace/v2/${encodeURIComponent(raw)}`
      );
      if (callResult.success) {
        setCurrentWip(callResult.data.wip || null);
        setIncomingPlus(callResult.data.incoming_plus || []);
        setLoading(false);
        return {
          ok: true,
          wip: callResult.data.wip,
          incoming_plus: callResult.data.incoming_plus || [],
        };
      } else {
        setLoading(false);
        setErr(callResult.data.message || "Lookup failed");
        setCurrentWip(null);
        setIncomingPlus([]);
        setCrumbs([]);
        if (callResult.errorStatus === 401) logout();
        return { ok: false, error: callResult.data.message || "Lookup failed" };
      }
    } else if (reportType === 2) {
      // Supplier item tracing
      setCurrentWip(null);
      setIncomingPlus([]);
      setCrumbs([]);
      setSelectedPlu(null);
      setByParent([]);
      setByLot([]);
      
      return await traceSupplierItem(raw);
    }
  };

  const loadPluDetails = async (wipId, plu) => {
    setErr("");
    setLoading(true);
    setByParent([]);
    setByLot([]);
    setDirectParents([]);
    let callResult = await makeGetApiCall(
      `trace/v2/${wipId}/plu/${encodeURIComponent(plu)}`
    );
    setLoading(false);
    if (callResult.success) {
      const by_parent = callResult.data.by_parent || [];
      const by_lot = callResult.data.by_lot || [];
      setByParent(by_parent);
      setByLot(by_lot);
      return { ok: true, by_parent, by_lot }; // <-- return the arrays
    } else {
      const msg = callResult.data.message || "Failed to load PLU details";
      setErr(msg);
      return { ok: false, error: msg };
    }
  };

  const loadDirectParents = async (wipId) => {
    setErr("");
    setLoading(true);
    setDirectParents([]);
    let callResult = await makeGetApiCall(`trace/v2/${wipId}/parents`);
    setLoading(false);
    if (callResult.success) {
      const parents = callResult.data.parents || [];
      setDirectParents(parents);
      return { ok: true, parents }; // <-- return the array
    } else {
      const msg = callResult.data.message || "Failed to load parents";
      setErr(msg);
      return { ok: false, error: msg };
    }
  };

  // ── NEW: raw fetchers for lazy tree building (no shared state changes)
  const fetchPluParents = async (wipId, plu) => {
    const r = await makeGetApiCall(
      `trace/v2/${wipId}/plu/${encodeURIComponent(plu)}`
    );
    if (!r.success)
      throw new Error(r.data?.message || "Failed to fetch PLU parents");
    return r.data; // { by_parent, by_lot }
  };

  const fetchDirectParents = async (wipId) => {
    const r = await makeGetApiCall(`trace/v2/${wipId}/parents`);
    if (!r.success)
      throw new Error(r.data?.message || "Failed to fetch parents");
    return r.data.parents; // array
  };

  // Supplier item tracing functions (Trace Children)
  const traceSupplierItem = async function (raw) {
    const requestId = ++latestSupplierRequestId.current;
    setLoading(true);
    setErr("");
    setLotNumber(null);
    setSource(null);
    setDirectChildren([]);
    setReceiving(null);

    try {
      const callResult = await makeGetApiCall(
        `trace/children/v2/${encodeURIComponent(raw)}`
      );

      if (requestId !== latestSupplierRequestId.current) {
        return { ok: false, stale: true };
      }

      if (!callResult.success) {
        const errorMsg = callResult.data?.message || "Lot number lookup failed";
        setErr(errorMsg);
        if (callResult.errorStatus === 401) logout();
        return { ok: false, error: errorMsg };
      }

      // Handle new API response structure per trace-child.md:
      // { lot_number, source: {...}, direct_children[] }
      // source is a single object (not an array)
      const data = callResult.data;
      
      const parsedSource = parseSupplierSource(data.source);
      
      const directChildren = (data.direct_children || []).map(parseDirectChild);
      
      setLotNumber(data.lot_number || raw);
      setSource(parsedSource);
      setDirectChildren(directChildren);
      return {
        ok: true,
        lot_number: data.lot_number || raw,
        source: parsedSource,
        direct_children: directChildren,
      };
    } catch {
      if (requestId !== latestSupplierRequestId.current) {
        return { ok: false, stale: true };
      }
      const errorMsg = "Lot number lookup failed";
      setErr(errorMsg);
      return { ok: false, error: errorMsg };
    } finally {
      if (requestId === latestSupplierRequestId.current) setLoading(false);
    }
  };

  const loadDirectChildren = async (wipId) => {
    // Don't set global loading state for lazy loading - component handles its own loading
    const callResult = await makeGetApiCall(
      `trace/children/v2/${wipId}/children`
    );
    if (callResult.success) {
      // Parse numeric values (API may return strings)
      const children = (callResult.data.children || []).map(parseDirectChild);
      return { ok: true, children };
    } else {
      const msg = callResult.data.message || "Failed to load children";
      if (callResult.errorStatus === 401) logout();
      return { ok: false, error: msg };
    }
  };

  const loadChildrenTree = async (wipId, maxDepth = 10) => {
    setErr("");
    setLoading(true);
    const callResult = await makeGetApiCall(
      `trace/children/v2/${wipId}/tree?maxDepth=${maxDepth}`
    );
    setLoading(false);
    if (callResult.success) {
      return { ok: true, tree: callResult.data };
    } else {
      const msg = callResult.data.message || "Failed to load tree";
      setErr(msg);
      if (callResult.errorStatus === 401) logout();
      return { ok: false, error: msg };
    }
  };

  const exportTraceability = async function (lot, reportType) {
    const callResult = await makePostApiCall(`exportTraceability/${lot}`, {
      type: reportType,
    });
    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal("Could not trace carton");
    }
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  return {
    // core
    doLookup,
    loading,
    exportTraceability,
    emptyModal,
    setEmptyModelOpen,
    lookup,
    setLookup,
    err,

    // context
    currentWip,
    setCurrentWip,
    incomingPlus,

    // selection
    selectedPlu,
    setSelectedPlu,

    // first-level detail
    loadPluDetails,
    byParent,
    byLot,

    // breadcrumbs
    crumbs,
    setCrumbs,

    // legacy (kept)
    loadDirectParents,
    directParents,

    // new lazy fetchers
    fetchPluParents,
    fetchDirectParents,

    // Supplier item tracing (Trace Children)
    lotNumber,
    source,
    directChildren,
    loadDirectChildren,
    loadChildrenTree,
    // Legacy receiving for backward compatibility
    receiving,
  };
}
